import { StatusBullet, type StatusBulletTone } from './StatusBullet.tsx'
import { Tag, type TagTone } from './Tag.tsx'
import {
  ruleOutcomesFor,
  type RuleOutcomeLine,
  type RuleOutcomeRecord,
} from '../lib/ruleOutcomes.ts'
import { RULE_OUTCOME_LABEL, RULE_SEVERITY_LABEL, RuleOutcome } from '../lib/vocabulary.ts'

/**
 * Every operational rule and what became of it on this formula (#497).
 *
 * The card above shows only the rules that fired, so a rule that ran and
 * raised nothing, one the account switched off and one that broke were all
 * the same silence, and nobody could tell whether the rules were running.
 *
 * Read from what the verification recorded when it was scored, never from
 * today's rules. A verification from before that record existed says so in
 * one line.
 *
 * Collapsed, like the ficha: reference, with nothing here to answer. The
 * fired rules are answered in the card.
 */
interface Look {
  bullet: StatusBulletTone
  tag: TagTone
  text: string
}

const QUIET: Readonly<Record<Exclude<RuleOutcome, RuleOutcome.Fired>, Look>> = {
  [RuleOutcome.Passed]: {
    bullet: 'neutral',
    tag: 'neutral',
    text: RULE_OUTCOME_LABEL[RuleOutcome.Passed],
  },
  [RuleOutcome.Disabled]: {
    bullet: 'neutral',
    tag: 'neutral',
    text: RULE_OUTCOME_LABEL[RuleOutcome.Disabled],
  },
  // Ours to fix, so not the red of a divergência: nothing is wrong with the requisição.
  [RuleOutcome.Failed]: {
    bullet: 'warning',
    tag: 'warning',
    text: RULE_OUTCOME_LABEL[RuleOutcome.Failed],
  },
}

/** A fired rule speaks with its own severity, as it does in the card. */
const FIRED: Readonly<Record<RuleOutcomeLine['severity'], Look>> = {
  error: { bullet: 'error', tag: 'danger', text: RULE_SEVERITY_LABEL.error },
  reminder: { bullet: 'information', tag: 'info', text: RULE_SEVERITY_LABEL.reminder },
}

function lookOf(line: RuleOutcomeLine): Look {
  return line.outcome === RuleOutcome.Fired ? FIRED[line.severity] : QUIET[line.outcome]
}

export function RuleOutcomes({
  outcomes,
  preparationId,
}: {
  /** Absent for a verification scored before outcomes were recorded. */
  outcomes: readonly RuleOutcomeRecord[] | undefined
  preparationId: string | undefined
}) {
  if (preparationId === undefined) return null

  if (outcomes === undefined)
    return (
      <p className="text-tSmall text-text-weak mt-4">
        Regras operacionais: sem registro das regras avaliadas.
      </p>
    )

  const lines = ruleOutcomesFor(outcomes, preparationId)

  return (
    <details className="border-stroke-weak mt-4 rounded-base border px-4 py-3">
      <summary className="text-tSmall text-text-weak cursor-pointer select-none">
        Regras operacionais verificadas ({lines.length})
      </summary>

      <RuleOutcomeList lines={lines} />
    </details>
  )
}

/**
 * The rules and what became of each, without the fold around them: for the
 * divergence-only pass, which folds several formulas' lists into one (#641).
 */
export function RuleOutcomeList({ lines }: { lines: readonly RuleOutcomeLine[] }) {
  return (
    <ul className="mt-2 flex flex-col">
      {lines.map((line) => {
        const look = lookOf(line)
        return (
          <li
            key={line.rule}
            className="border-stroke-weak flex items-center gap-2 border-t py-1.5 first:border-t-0"
          >
            <StatusBullet tone={look.bullet} />
            <span
              className={`text-tSmall min-w-0 flex-1 break-words ${line.outcome === RuleOutcome.Disabled ? 'text-text-weak' : 'text-text-strong'}`}
            >
              {line.title}
              {line.requisitionWide && <span className="text-text-weak"> · na requisição</span>}
            </span>
            <Tag
              tone={look.tag}
              variant={line.outcome === RuleOutcome.Disabled ? 'stroke' : 'filled'}
              title={line.error}
            >
              {look.text}
            </Tag>
          </li>
        )
      })}
    </ul>
  )
}
